import { FC, useState, useEffect } from 'react'

import styled from 'styled-components'

import { TransportCalcItem } from 'src/Pages/Calculator/TransportCalcItem.tsx'
import { MultiSelect } from 'src/Components/MultiSelect'
import {
  ModelCalculated,
  OfferModelUnit,
} from 'src/Pages/Calculator/useCalculator.tsx'
import { Model } from 'src/Redux/api/modelApi.ts'
import { colors, device } from 'src/Helpers/constants.ts'

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  border-radius: 5px;
`

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledMultiSelect = styled(MultiSelect)`
  width: 100%;
`

const SearchBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 20px;
  max-width: 600px;
  width: 100%;

  @media ${device.tabletMax} {
    max-width: 350px;
  }
`

const Text = styled.div`
  font-size: 16px;
  width: 100%;
  text-align: center;
  color: ${colors.thirdGray};
  font-weight: 600;
  margin: 0 auto;
`

type UpdateItemPropertyFn = <T>(
  itemId: number,
  property: keyof ModelCalculated,
  value: T,
) => void

interface EquipmentsListProps {
  models?: Model[]
  onItemClick: (modelId: string | null) => void
  setSearchTerm: (searchTerm: string) => void
  searchTerm: string
  items: ModelCalculated[]
  onDeleteClick: (modelId: number) => void
  updateItemProperty: UpdateItemPropertyFn
  equipmentError?: string
  onAddUnit: (modelId: number) => void
  onRemoveUnit: (modelId: number, unitIndex: number) => void
  onUpdateUnit: <T extends keyof OfferModelUnit>(
    modelId: number,
    unitIndex: number,
    property: T,
    value: OfferModelUnit[T],
  ) => void
}

export const ModelsList: FC<EquipmentsListProps> = ({
  models,
  onItemClick,
  setSearchTerm,
  searchTerm,
  items,
  onDeleteClick,
  updateItemProperty,
  equipmentError,
  onAddUnit,
  onRemoveUnit,
  onUpdateUnit,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([])

  const formattedEquipment =
    models?.map((model) => ({
      value: model.id.toString(),
      label: `${model.modelType.label} ${model.name}`,
    })) || []

  useEffect(() => {
    const currentSelectedValues = items.map((item) => item.id.toString())
    setSelectedValues(currentSelectedValues)
  }, [items])

  return (
    <ListContainer>
      <SearchBlock>
        <StyledMultiSelect
          placeholder='Название техники'
          data={formattedEquipment}
          value={selectedValues}
          data-testid='select-equipment'
          onChange={(values) => {
            if (!values) values = []
            const removedItems = selectedValues.filter(
              (id) => !values.includes(id),
            )
            const addedItems = values.filter(
              (id) => !selectedValues.includes(id),
            )
            removedItems.forEach((itemId) => {
              onDeleteClick(Number(itemId))
            })
            addedItems.forEach((itemId) => {
              onItemClick(itemId)
            })
            setSelectedValues(values)
          }}
          searchable
          onSearchChange={setSearchTerm}
          searchValue={searchTerm}
          withCheckIcon={false}
          error={equipmentError}
          hidePickedOptions
        />
      </SearchBlock>

      <ListWrapper>
        {items.map((model) => {
          return (
            <TransportCalcItem
              key={model.id}
              model={model}
              onDeleteClick={() => {
                onDeleteClick(model.id)
              }}
              onPriceChange={(e) => {
                updateItemProperty(
                  model.id,
                  'specialPrice',
                  Number(e.target.value),
                )
              }}
              onDetalizationChange={(e) => {
                updateItemProperty(model.id, 'detalization', e.target.checked)
              }}
              onSelectImage={(url) =>
                updateItemProperty(model.id, 'selectedImageUrl', url)
              }
              onAddUnit={() => onAddUnit(model.id)}
              onRemoveUnit={(unitIndex: number) =>
                onRemoveUnit(model.id, unitIndex)
              }
              onUpdateUnit={(
                unitIndex: number,
                property: keyof OfferModelUnit,
                value: OfferModelUnit[keyof OfferModelUnit],
              ) => onUpdateUnit(model.id, unitIndex, property, value)}
            />
          )
        })}

        {items.length === 0 && <Text>Добавьте технику для расчета</Text>}
      </ListWrapper>
    </ListContainer>
  )
}
